(() => {
  if (customElements.get('nl-reviews')) return;
  const requests = new Map();
  const node = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };
  const url = value => {
    try { const result = new URL(value,location.origin); return result.protocol === 'https:' ? result.href : ''; } catch { return ''; }
  };
  class NeonReviews extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.config = JSON.parse(this.querySelector('[data-nl-config]').textContent);
      this.labels = this.config.labels;
      this.grid = this.querySelector('[data-nl-grid]');
      this.status = this.querySelector('[data-nl-status]');
      this.more = this.querySelector('[data-nl-more]');
      this.more.addEventListener('click',()=>this.append(true));
      this.dialog = this.querySelector('dialog');
      this.dialog.addEventListener('click',event=>{if(event.target === this.dialog)this.dialog.close();});
      this.querySelector('[data-nl-close]').addEventListener('click',()=>this.dialog.close());
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries=>{if(entries.some(e=>e.isIntersecting)){this.observer.disconnect();this.load();}},{rootMargin:'600px'});
        this.observer.observe(this);
      } else this.load();
    }
    disconnectedCallback() { this.observer?.disconnect(); }
    text(key,vars={}) {
      let result=this.labels[key]||key;
      for(const [key,value] of Object.entries(vars))result=result.replaceAll('%%'+key+'%%',String(value)).replaceAll('{{ '+key+' }}',String(value));
      return result;
    }
    async load() {
      this.status.textContent=this.labels.loading;
      try {
        if(!requests.has(this.config.url))requests.set(this.config.url,fetch(this.config.url,{credentials:'same-origin'}).then(r=>{if(!r.ok)throw new Error('review data unavailable');return r.json();}).catch(error=>{requests.delete(this.config.url);throw error;}));
        const data=await requests.get(this.config.url);
        const live=JSON.parse(this.querySelector('[data-nl-live]').textContent||'null');
        let all=NeonReviewTools.prepare(data,live?.reviews);
        if(this.config.audience==='business' && this.config.featuredUrl) {
          if(!requests.has(this.config.featuredUrl))requests.set(this.config.featuredUrl,fetch(this.config.featuredUrl,{credentials:'same-origin'}).then(r=>{if(!r.ok)throw new Error('featured review data unavailable');return r.json();}).catch(error=>{requests.delete(this.config.featuredUrl);throw error;}));
          const featured=await requests.get(this.config.featuredUrl);
          const replaced=new Set(featured.replaces||[]);
          all=NeonReviewTools.deduplicate([...featured.reviews,...all.filter(review=>!replaced.has(review.id))]);
        }
        this.reviews=NeonReviewTools.select(all,this.config.audience,this.config.city,data.rules);
        this.shown=0;
        this.grid.replaceChildren();
        if(!this.reviews.length){this.status.textContent=this.labels.empty;return;}
        const average=this.reviews.reduce((total,r)=>total+r.rating,0)/this.reviews.length;
        const summary=this.querySelector('[data-nl-summary]');
        summary.textContent=average.toFixed(1)+' / 5 · '+this.text('count',{count:this.reviews.length.toLocaleString()});
        summary.hidden=false;
        this.append(false);
      } catch(error) {
        this.status.textContent=this.labels.error;
        const retry=node('button','nl-reviews__more',this.labels.retry);
        retry.type='button';retry.addEventListener('click',()=>{retry.remove();this.load();});this.status.after(retry);
      }
    }
    stars(rating) {
      const stars=node('span','nl-reviews__stars','★'.repeat(rating)+'☆'.repeat(5-rating));
      stars.setAttribute('role','img');stars.setAttribute('aria-label',this.text('stars',{rating}));return stars;
    }
    card(review) {
      const card=node('article','nl-reviews__card');card.dataset.reviewId=review.id;
      if(review.images.length) {
        const button=node('button','nl-reviews__photo');button.type='button';button.setAttribute('aria-label',this.labels.view_photo+': '+review.product);
        const image=node('img');image.src=url(review.images[0]);image.alt=this.text('photo_alt',{product:review.product});image.loading='lazy';image.decoding='async';image.width=600;image.height=600;
        button.append(image);button.addEventListener('click',()=>this.showReview(review));card.append(button);
      }
      const content=node('div','nl-reviews__content');
      const top=node('div','nl-reviews__reviewer');top.append(node('strong','nl-reviews__name',review.name));
      if(review.verified){const badge=node('span','nl-reviews__verified','✓');badge.title=this.labels.verified;badge.setAttribute('aria-label',this.labels.verified);top.append(badge);}
      content.append(top,this.stars(review.rating));
      if(review.date){const date=new Date(review.date);if(!Number.isNaN(date.getTime())){const time=node('time','nl-reviews__date',new Intl.DateTimeFormat(document.documentElement.lang||'en',{year:'numeric',month:'short',day:'numeric'}).format(date));time.dateTime=date.toISOString();content.append(time);}}
      if(review.title)content.append(node('h3','nl-reviews__title',review.title));
      if(review.body){const body=node('p','nl-reviews__body',review.body);content.append(body);if(review.body.length>220){const details=node('button','nl-reviews__details',this.labels.details);details.type='button';details.addEventListener('click',()=>this.showReview(review));content.append(details);}}
      if(review.handle && /^[a-z0-9-]+$/i.test(review.handle)){const product=node('a','nl-reviews__product',this.labels.reviewed+': '+review.product);product.href=this.config.root.replace(/\/?$/, '/')+'products/'+encodeURIComponent(review.handle);content.append(product);}
      card.append(content);return card;
    }
    append(focus) {
      const next=this.reviews.slice(this.shown,this.shown+this.config.pageSize);
      const fragment=document.createDocumentFragment();next.forEach(review=>fragment.append(this.card(review)));
      const first=fragment.firstElementChild;this.grid.append(fragment);this.shown+=next.length;
      this.status.textContent=this.text('showing',{shown:this.shown,total:this.reviews.length.toLocaleString()});
      this.more.hidden=this.shown>=this.reviews.length;
      if(focus&&first){first.tabIndex=-1;first.focus({preventScroll:true});}
    }
    showReview(review) {
      const target=this.querySelector('[data-nl-dialog-content]');target.replaceChildren();
      if(review.images.length){const gallery=node('div','nl-reviews__gallery');for(const source of review.images){const image=node('img');image.src=url(source);image.alt=this.text('photo_alt',{product:review.product});image.loading='lazy';gallery.append(image);}target.append(gallery);}
      const content=node('div','nl-reviews__dialog-copy');content.append(node('h3','',review.name),this.stars(review.rating));
      if(review.title)content.append(node('p','nl-reviews__title',review.title));
      if(review.body)content.append(node('p','',review.body));
      content.append(node('p','nl-reviews__product',this.labels.reviewed+': '+review.product));target.append(content);
      this.dialog.showModal();
    }
  }
  customElements.define('nl-reviews',NeonReviews);
})();
